// src/features/banking/pages/BankAccountsPage.tsx
// ENHANCED UI/UX - WITH BUSINESS & BRANCH LINKING + CACHE INVALIDATION
// ✅ FIXED: Edit mode disables immutable fields; shows clear note

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import * as bankingApi from '../services/bankingApi';
import * as businessApi from '@/features/business/services/businessApi';
import * as settingsApi from '@/features/settings/services/settingsApi';
import type { BankAccount } from '../services/bankingApi';
import type { Business } from '@/features/business/services/businessApi';
import {
    CreditCard, Plus, Save, Building2, Store,
    ArrowLeft, Trash2, Pencil, CheckCircle2,
    Search, X, Eye, Wallet, PiggyBank, Landmark,
    DollarSign, Layers, ArrowUp, ArrowDown,
    Star, RefreshCw, LayoutGrid, List, Sparkles,
    Rocket, PartyPopper, BadgeCheck,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import React from 'react';

// ============================================================
// FORMATTERS
// ============================================================

const formatNaira = (val: string | number) => {
    const num = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(num)) return '₦0.00';
    if (num >= 1000000) return `₦${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `₦${(num / 1000).toFixed(1)}K`;
    return `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
};

// ============================================================
// HELPERS
// ============================================================

const extractErrorMessage = (err: any): string => {
    const apiError = err?.response?.data?.error;
    if (!apiError) return err?.message || 'An unexpected error occurred.';
    if (apiError.details && Array.isArray(apiError.details) && apiError.details.length > 0) {
        return apiError.details.map((d: any) => d.message).join('. ');
    }
    return apiError.message || 'An unexpected error occurred.';
};

const extractFieldErrors = (err: any): Record<string, string> => {
    const apiError = err?.response?.data?.error;
    if (!apiError?.details || !Array.isArray(apiError.details)) return {};
    const fieldErrors: Record<string, string> = {};
    apiError.details.forEach((detail: any) => { if (detail.field && detail.message) fieldErrors[detail.field] = detail.message; });
    return fieldErrors;
};

// ============================================================
// CELEBRATION OVERLAY
// ============================================================

function CelebrationOverlay({ show, accountName, onComplete }: { show: boolean; accountName: string; onComplete: () => void }) {
    useEffect(() => {
        if (show) { const timer = setTimeout(onComplete, 3000); return () => clearTimeout(timer); }
    }, [show, onComplete]);
    if (!show) return null;
    const colors = ['#10B981', '#06B6D4', '#8B5CF6', '#F59E0B', '#EC4899', '#3B82F6', '#EF4444'];
    const pieces = Array.from({ length: 50 }, (_, i) => ({ id: i, left: `${Math.random() * 100}%`, delay: Math.random() * 2, duration: 1.5 + Math.random() * 2, color: colors[Math.floor(Math.random() * colors.length)], size: 8 + Math.random() * 12 }));
    return (
        <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] pointer-events-none overflow-hidden">
                {pieces.map(p => (<motion.div key={p.id} initial={{ y: -20, opacity: 1 }} animate={{ y: '100vh', x: `${(Math.random() - 0.5) * 200}px`, opacity: 0, rotate: 360 }} transition={{ duration: p.duration, delay: p.delay, ease: 'easeIn' }} className="absolute top-0 rounded-sm" style={{ left: p.left, width: p.size, height: p.size * 0.6, backgroundColor: p.color }} />))}
                <motion.div initial={{ scale: 0, y: -50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0, y: -50 }} transition={{ type: 'spring', stiffness: 200, damping: 15 }} className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto">
                    <div className="bg-white rounded-3xl shadow-2xl p-8 text-center border-2 border-emerald-200">
                        <motion.div animate={{ rotate: [0, -10, 10, -10, 0] }} transition={{ duration: 0.8, repeat: Infinity, repeatDelay: 1 }} className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-200"><PartyPopper size={40} className="text-white" /></motion.div>
                        <h2 className="text-2xl font-black text-slate-900 mb-1">💰 Account Added!</h2>
                        <p className="text-slate-500 font-medium"><strong className="text-emerald-600">{accountName}</strong> is ready!</p>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

// ============================================================
// ACCOUNT TYPE CONFIG
// ============================================================

const accountTypeConfig: Record<string, { label: string; icon: React.ElementType; gradient: string; bgLight: string; borderColor: string; textColor: string; cardGradient: string }> = {
    savings: { label: 'Savings', icon: PiggyBank, gradient: 'from-emerald-500 to-teal-600', bgLight: 'bg-emerald-50', borderColor: 'border-emerald-200', textColor: 'text-emerald-700', cardGradient: 'from-emerald-50 via-white to-teal-50' },
    current: { label: 'Current', icon: Landmark, gradient: 'from-blue-500 to-indigo-600', bgLight: 'bg-blue-50', borderColor: 'border-blue-200', textColor: 'text-blue-700', cardGradient: 'from-blue-50 via-white to-indigo-50' },
    cash: { label: 'Cash', icon: Wallet, gradient: 'from-amber-500 to-orange-600', bgLight: 'bg-amber-50', borderColor: 'border-amber-200', textColor: 'text-amber-700', cardGradient: 'from-amber-50 via-white to-orange-50' },
    other: { label: 'Other', icon: CreditCard, gradient: 'from-purple-500 to-violet-600', bgLight: 'bg-purple-50', borderColor: 'border-purple-200', textColor: 'text-purple-700', cardGradient: 'from-purple-50 via-white to-violet-50' },
};

// ============================================================
// BALANCE CARD
// ============================================================

function BalanceCard({ account, onClick, onEdit, onDelete }: { account: BankAccount; onClick: () => void; onEdit: () => void; onDelete: () => void }) {
    const cfg = accountTypeConfig[account.accountType] || accountTypeConfig.other;
    const Icon = cfg.icon;
    const balance = parseFloat(account.currentBalance || '0');
    const openingBalance = parseFloat(account.openingBalance || '0');
    const growth = openingBalance > 0 ? ((balance - openingBalance) / openingBalance) * 100 : 0;
    return (
        <motion.div whileHover={{ y: -6, scale: 1.01 }} onClick={onClick}
            className={clsx('relative overflow-hidden rounded-2xl border-2 shadow-sm p-5 transition-all duration-300 cursor-pointer group flex flex-col border-slate-200 hover:shadow-xl hover:border-emerald-300', `bg-gradient-to-br ${cfg.cardGradient}`)}>
            <div className="absolute top-0 right-0 w-32 h-32 opacity-[0.03]"><Icon size={128} className="text-slate-900" /></div>
            <div className="relative flex items-start justify-between mb-4"><div className={`p-3 rounded-2xl bg-gradient-to-br ${cfg.gradient} shadow-lg`}><Icon size={24} className="text-white" /></div><div className="flex flex-col items-end gap-1">{account.isDefault && <Badge variant="success" size="sm">Default</Badge>}<Badge variant={account.isActive ? 'success' : 'danger'} size="sm">{account.isActive ? 'Active' : 'Inactive'}</Badge></div></div>
            <div className="relative"><h3 className="font-bold text-slate-900 text-lg">{account.bankName}</h3><p className="text-sm text-slate-500 mt-0.5 font-mono tracking-wide">{account.accountNumber}</p><p className="text-xs text-slate-400 mt-0.5">{account.accountName}</p></div>
            <div className="flex-1" />
            <div className="relative mt-4 p-4 bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-100">
                <div className="flex items-center justify-between mb-1"><span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Balance</span><span className={clsx('text-[10px] font-bold flex items-center gap-0.5', growth >= 0 ? 'text-emerald-500' : 'text-rose-500')}>{growth >= 0 ? <ArrowUp size={10} /> : <ArrowDown size={10} />}{Math.abs(growth).toFixed(1)}%</span></div>
                <motion.p key={balance} initial={{ scale: 1.1, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-2xl lg:text-3xl font-black text-emerald-600 tracking-tight">{formatNaira(account.currentBalance)}</motion.p>
                <p className="text-[10px] text-slate-400 mt-1">Opening: {formatNaira(account.openingBalance)}</p>
                <div className="flex items-center gap-2 mt-2"><Badge variant="neutral" size="sm">{cfg.label}</Badge><span className="text-[10px] text-slate-400">{account.currency}</span></div>
            </div>
            {(account.branchName || account.businessName) && <div className="relative flex items-center gap-3 mt-3 text-xs text-slate-400">{account.branchName && <span className="flex items-center gap-1"><Store size={10} />{account.branchName}</span>}{account.businessName && <span className="flex items-center gap-1"><Building2 size={10} />{account.businessName}</span>}</div>}
            <div className="relative flex gap-2 mt-4 pt-3 border-t border-slate-200/60 opacity-0 group-hover:opacity-100 transition-all duration-200">
                <button onClick={(e) => { e.stopPropagation(); onClick(); }} className="flex-1 flex items-center justify-center gap-1 text-xs font-medium text-slate-600 hover:bg-slate-100 py-2 rounded-lg"><Eye size={12} />Details</button>
                <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="flex-1 flex items-center justify-center gap-1 text-xs font-medium text-slate-500 hover:bg-slate-100 py-2 rounded-lg"><Pencil size={12} />Edit</button>
                <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="flex-1 flex items-center justify-center gap-1 text-xs font-medium text-rose-500 hover:bg-rose-50 py-2 rounded-lg"><Trash2 size={12} />Delete</button>
            </div>
        </motion.div>
    );
}

// ============================================================
// STAT CARD
// ============================================================

function StatCard({ label, value, icon: Icon, gradient, sublabel }: { label: string; value: string | number; icon: React.ElementType; gradient: string; sublabel?: string }) {
    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -3 }} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-lg transition-all">
            <div className="flex items-start justify-between mb-3"><div className={`p-2.5 rounded-xl bg-gradient-to-br ${gradient} shadow-md`}><Icon className="w-5 h-5 text-white" /></div></div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</p>
            <p className="text-xl font-bold text-slate-900 mt-1">{typeof value === 'number' ? value.toLocaleString() : value}</p>
            {sublabel && <p className="text-[10px] text-slate-400 mt-0.5">{sublabel}</p>}
        </motion.div>
    );
}

// ============================================================
// SKELETON
// ============================================================

function PageSkeleton() {
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
            <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-5">
                <div className="rounded-3xl bg-slate-200 animate-pulse h-40" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => (<div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 animate-pulse h-28" />))}</div>
            </div>
        </div>
    );
}

// ============================================================
// MAIN PAGE
// ============================================================

export function BankAccountsPage() {
    const navigate = useNavigate();
    const tenantId = useAuthStore(s => s.user?.tenantId);
    const queryClient = useQueryClient();
    const addToast = useToastStore(s => s.addToast);

    const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
    const [showForm, setShowForm] = useState(false);
    const [editingAccount, setEditingAccount] = useState<BankAccount | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<BankAccount | null>(null);
    const [search, setSearch] = useState('');
    const [filterBusinessId, setFilterBusinessId] = useState('');
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [showCelebration, setShowCelebration] = useState(false);
    const [newAccountName, setNewAccountName] = useState('');

    const [form, setForm] = useState({
        bankName: '', accountNumber: '', accountName: '', accountType: 'current',
        currency: 'NGN', openingBalance: '0', branchId: '', businessId: '', isDefault: false
    });

    // ==================== QUERIES ====================

    const { data: accounts, isLoading, isError, refetch } = useQuery({
        queryKey: ['bank-accounts', tenantId],
        queryFn: () => bankingApi.getBankAccounts(),
        enabled: !!tenantId,
    });

    const { data: businesses } = useQuery<Business[]>({
        queryKey: ['businesses-active', tenantId],
        queryFn: businessApi.getBusinessesWithStats,
        enabled: !!tenantId,
    });

    const { data: branches } = useQuery<any[]>({
        queryKey: ['branches', tenantId],
        queryFn: () => settingsApi.getBranches(tenantId!),
        enabled: !!tenantId,
    });

    // ==================== DERIVED DATA ====================

    const allBranches = (() => {
        const raw = branches as any;
        if (Array.isArray(raw)) return raw;
        if (raw?.data && Array.isArray(raw.data)) return raw.data;
        return [];
    })();

    const branchesForBusiness = (businessId: string) =>
        allBranches.filter((b: any) => b.businessId === businessId);

    // ==================== MUTATIONS ====================

    const saveMutation = useMutation({
        mutationFn: async () => {
            if (editingAccount) {
                // ✅ Only send editable fields
                const payload: Record<string, unknown> = {
                    accountName: form.accountName.trim(),
                    accountType: form.accountType,
                    currency: form.currency,
                    isDefault: form.isDefault,
                    isActive: true, // keep active; or use a separate toggle if needed
                };
                return bankingApi.updateBankAccount(editingAccount.id, payload);
            }

            // Create payload includes all fields
            const payload: any = {
                bankName: form.bankName.trim(),
                accountNumber: form.accountNumber.trim(),
                accountName: form.accountName.trim(),
                accountType: form.accountType,
                currency: form.currency,
                openingBalance: parseFloat(form.openingBalance) || 0,
                isDefault: form.isDefault,
                businessId: form.businessId || undefined,
                branchId: form.branchId || undefined,
            };
            return bankingApi.createBankAccount(payload);
        },
        onSuccess: () => {
            // ✅ Invalidate all relevant caches
            queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
            queryClient.invalidateQueries({ queryKey: ['businesses'] });
            queryClient.invalidateQueries({ queryKey: ['businesses-stats'] });
            queryClient.invalidateQueries({ queryKey: ['business'] });
            queryClient.invalidateQueries({ queryKey: ['branches'] });

            const name = form.bankName || form.accountName;
            addToast({ type: 'success', title: editingAccount ? 'Account Updated ✨' : 'Account Created! 💰', message: editingAccount ? `${name} updated.` : `${name} is ready for transactions.`, action: !editingAccount ? { label: 'View Account', onClick: () => navigate('/bank-accounts') } : undefined });
            if (!editingAccount) { setNewAccountName(name); setShowCelebration(true); }
            setShowForm(false); setEditingAccount(null); setFormErrors({});
        },
        onError: (err: any) => {
            const fe = extractFieldErrors(err);
            if (Object.keys(fe).length > 0) setFormErrors(fe);
            else { const m = extractErrorMessage(err).toLowerCase(); if (m.includes('account number')) setFormErrors({ accountNumber: extractErrorMessage(err) }); else if (m.includes('bank name')) setFormErrors({ bankName: extractErrorMessage(err) }); }
            addToast({ type: 'error', title: 'Save Failed', message: extractErrorMessage(err) });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => bankingApi.deleteBankAccount(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
            queryClient.invalidateQueries({ queryKey: ['businesses'] });
            queryClient.invalidateQueries({ queryKey: ['businesses-stats'] });
            queryClient.invalidateQueries({ queryKey: ['business'] });
            queryClient.invalidateQueries({ queryKey: ['branches'] });
            addToast({ type: 'success', title: 'Account Deleted', message: 'The account has been removed.' });
            setDeleteConfirm(null);
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Delete Failed', message: extractErrorMessage(err) }),
    });

    // ==================== HELPERS ====================

    const updateFormField = (field: string, value: string | boolean) => {
        setForm(p => ({ ...p, [field]: value }));
        if (formErrors[field]) setFormErrors(p => { const u = { ...p }; delete u[field]; return u; });
    };

    const handleEdit = (a: BankAccount) => {
        setEditingAccount(a);
        setForm({
            bankName: a.bankName,
            accountNumber: a.accountNumber,
            accountName: a.accountName,
            accountType: a.accountType,
            currency: a.currency,
            openingBalance: a.openingBalance,
            branchId: a.branchId || '',
            businessId: a.businessId || '',
            isDefault: a.isDefault,
        });
        setFormErrors({});
        setShowForm(true);
    };

    const resetForm = () => {
        setForm({ bankName: '', accountNumber: '', accountName: '', accountType: 'current', currency: 'NGN', openingBalance: '0', branchId: '', businessId: '', isDefault: false });
        setFormErrors({});
        setShowForm(false);
        setEditingAccount(null);
    };

    // When business changes, clear branch if not valid
    const handleBusinessChange = (businessId: string) => {
        setForm(prev => ({
            ...prev,
            businessId,
            branchId: '', // reset branch when business changes
        }));
    };

    // ==================== LOADING / ERROR ====================

    if (isLoading) return <PageSkeleton />;
    if (isError) return <div className="min-h-screen flex items-center justify-center p-4"><ErrorState title="Failed to load accounts" onRetry={() => refetch()} /></div>;

    const accountsList = (accounts || []).filter(a => {
        if (filterBusinessId && a.businessId !== filterBusinessId) return false;
        if (!search) return true;
        const q = search.toLowerCase();
        return a.bankName.toLowerCase().includes(q) ||
            a.accountNumber.includes(q) ||
            a.accountName.toLowerCase().includes(q);
    });
    const totalBalance = accountsList.reduce((s, a) => s + parseFloat(a.currentBalance || '0'), 0);
    const activeCount = accountsList.filter(a => a.isActive).length;
    const typeBreakdown = accountsList.reduce((acc, a) => { const t = a.accountType || 'other'; if (!acc[t]) acc[t] = { count: 0, balance: 0 }; acc[t].count++; acc[t].balance += parseFloat(a.currentBalance || '0'); return acc; }, {} as Record<string, { count: number; balance: number }>);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
            <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-5">
                <CelebrationOverlay show={showCelebration} accountName={newAccountName} onComplete={() => setShowCelebration(false)} />

                {/* HEADER - Business Theme (Emerald/Teal) */}
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                    <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-teal-600 mb-3 group"><ArrowLeft size={16} className="group-hover:-translate-x-1" />Back to Dashboard</button>
                    <div className="relative overflow-hidden bg-gradient-to-br from-teal-500 via-emerald-600 to-teal-700 rounded-3xl shadow-2xl">
                        <div className="absolute inset-0"><div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.15),transparent_50%)]" /><div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" /></div>
                        <div className="relative px-6 lg:px-10 py-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                            <div className="flex items-center gap-4">
                                <motion.div whileHover={{ rotate: -5, scale: 1.05 }} className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur-sm ring-1 ring-white/20 flex items-center justify-center shadow-xl"><CreditCard size={32} className="text-white" /></motion.div>
                                <div><h1 className="text-3xl lg:text-4xl font-black text-white tracking-tight">Bank Accounts</h1><p className="text-teal-100 text-sm mt-1 font-medium">{accountsList.length} account{accountsList.length !== 1 ? 's' : ''} · {activeCount} active · Total: <span className="font-bold text-white">{formatNaira(totalBalance)}</span></p></div>
                            </div>
                            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                                <Button size="md" className="!bg-white !text-emerald-700 hover:!text-emerald-800 !font-bold !px-6 !py-3 !rounded-2xl !text-base !border-2 !border-white/80 !shadow-xl !shadow-black/20 hover:!shadow-black/30 hover:!bg-emerald-50 transition-all duration-300" leftIcon={<Plus size={18} />} onClick={() => { resetForm(); setShowForm(true); }}>Add Account</Button>
                            </motion.div>
                        </div>
                    </div>
                </motion.div>

                {/* STATS */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard label="Total Balance" value={formatNaira(totalBalance)} icon={DollarSign} gradient="from-emerald-500 to-teal-600" sublabel={`${activeCount} active`} />
                    <StatCard label="Active" value={`${activeCount}/${accountsList.length}`} icon={BadgeCheck} gradient="from-green-500 to-emerald-600" />
                    <StatCard label="Account Types" value={Object.keys(typeBreakdown).length} icon={Layers} gradient="from-blue-500 to-indigo-600" />
                    <StatCard label="Default" value={accountsList.filter(a => a.isDefault).length} icon={Star} gradient="from-amber-500 to-orange-600" />
                </div>

                {/* TYPE BREAKDOWN */}
                {Object.keys(typeBreakdown).length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {Object.entries(typeBreakdown).map(([type, data]) => {
                            const cfg = accountTypeConfig[type] || accountTypeConfig.other;
                            const TypeIcon = cfg.icon;
                            return (<motion.div key={type} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -3 }} className={`${cfg.bgLight} rounded-2xl border-2 ${cfg.borderColor} p-5`}><div className="flex items-center gap-3 mb-3"><div className={`p-2.5 rounded-xl bg-gradient-to-br ${cfg.gradient} shadow-md`}><TypeIcon className="w-5 h-5 text-white" /></div><div><p className="text-xs font-bold text-slate-500 uppercase">{cfg.label}</p><p className={`text-lg font-black ${cfg.textColor}`}>{formatNaira(data.balance)}</p></div></div><p className="text-[10px] text-slate-400">{data.count} account{data.count !== 1 ? 's' : ''}</p></motion.div>);
                        })}
                    </div>
                )}

                {/* TOOLBAR */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm sticky top-4 z-30 backdrop-blur-xl bg-white/95 p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
                        <div className="relative flex-1 sm:flex-initial">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search accounts..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs w-full sm:w-60 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                            />
                            {search && (
                                <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                    <X size={12} />
                                </button>
                            )}
                        </div>
                        <select
                            value={filterBusinessId}
                            onChange={e => setFilterBusinessId(e.target.value)}
                            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:ring-2 focus:ring-teal-500/20">
                            <option value="">All Businesses</option>
                            {(businesses || []).map(b => (
                                <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-center gap-2 self-end sm:self-auto">
                        <div className="flex items-center bg-slate-100 rounded-xl p-1">
                            <button onClick={() => setViewMode('grid')} className={clsx('flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all', viewMode === 'grid' ? 'bg-white shadow-xs text-teal-700' : 'text-slate-500')}><LayoutGrid size={14} />Grid</button>
                            <button onClick={() => setViewMode('table')} className={clsx('flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all', viewMode === 'table' ? 'bg-white shadow-xs text-teal-700' : 'text-slate-500')}><List size={14} />Table</button>
                        </div>
                        <button onClick={() => refetch()} className="p-2 rounded-xl border border-slate-200 text-slate-400 hover:text-teal-700 hover:bg-slate-50 transition-colors" title="Refresh"><RefreshCw size={14} /></button>
                    </div>
                </div>

                {/* ACCOUNTS LIST */}
                {accountsList.length === 0 ? (
                    <EmptyState icon={<CreditCard size={48} />} title={search ? 'No accounts match' : 'No bank accounts'} description={search ? 'Try a different search.' : 'Add your first bank account.'} action={search ? { label: 'Clear', onClick: () => setSearch('') } : { label: 'Add Account', onClick: () => setShowForm(true) }} />
                ) : viewMode === 'table' ? (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <table className="min-w-full divide-y divide-slate-100"><thead><tr className="bg-slate-50"><th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase">Account</th><th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase hidden md:table-cell">Type</th><th className="px-6 py-4 text-right text-[10px] font-bold text-slate-500 uppercase">Balance</th><th className="px-6 py-4 text-center text-[10px] font-bold text-slate-500 uppercase">Status</th><th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase hidden lg:table-cell">Branch/Business</th><th className="px-6 py-4 text-right text-[10px] font-bold text-slate-500 uppercase">Actions</th></tr></thead>
                            <tbody className="divide-y divide-slate-50">
                                {accountsList.map(a => { const cfg = accountTypeConfig[a.accountType] || accountTypeConfig.other; const TypeIcon = cfg.icon; return (<motion.tr key={a.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} onClick={() => navigate(`/bank-accounts/${a.id}`)} className="hover:bg-slate-50 cursor-pointer group"><td className="px-6 py-4"><div className="flex items-center gap-3"><div className={`p-2.5 rounded-xl bg-gradient-to-br ${cfg.gradient} shadow-md`}><TypeIcon size={18} className="text-white" /></div><div className="min-w-0"><p className="text-sm font-bold text-slate-900 truncate group-hover:text-teal-600">{a.bankName}</p><p className="text-xs text-slate-400 font-mono">{a.accountNumber}</p><p className="text-xs text-slate-400 truncate">{a.accountName}</p></div></div></td><td className="px-6 py-4 hidden md:table-cell"><Badge variant="neutral" size="sm">{cfg.label}</Badge></td><td className="px-6 py-4 text-right"><span className="text-sm font-bold text-emerald-600">{formatNaira(a.currentBalance)}</span></td><td className="px-6 py-4 text-center"><Badge variant={a.isActive ? 'success' : 'danger'} size="sm">{a.isActive ? 'Active' : 'Inactive'}</Badge></td><td className="px-6 py-4 hidden lg:table-cell"><div className="text-xs text-slate-500 space-y-0.5">{a.branchName && <p className="flex items-center gap-1"><Store size={10} />{a.branchName}</p>}{a.businessName && <p className="flex items-center gap-1"><Building2 size={10} />{a.businessName}</p>}</div></td><td className="px-6 py-4 text-right"><div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100"><button onClick={e => { e.stopPropagation(); handleEdit(a); }} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600"><Pencil size={14} /></button><button onClick={e => { e.stopPropagation(); setDeleteConfirm(a); }} className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button></div></td></motion.tr>); })}
                            </tbody></table>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">{accountsList.map(a => (<BalanceCard key={a.id} account={a} onClick={() => navigate(`/bank-accounts/${a.id}`)} onEdit={() => handleEdit(a)} onDelete={() => setDeleteConfirm(a)} />))}</div>
                )}

                {/* CREATE/EDIT MODAL */}
                <AnimatePresence>
                    {showForm && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={resetForm}>
                            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} onClick={e => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                                <div className="sticky top-0 bg-white rounded-t-3xl z-10"><div className="px-8 py-5 bg-gradient-to-r from-teal-500 to-emerald-600 rounded-t-3xl"><div className="flex items-center justify-between"><div className="flex items-center gap-3"><div className="p-2 rounded-xl bg-white/20"><CreditCard size={20} className="text-white" /></div><div><h2 className="text-lg font-bold text-white">{editingAccount ? 'Edit Account' : 'Add Bank Account'}</h2><p className="text-teal-100 text-xs">{editingAccount ? 'Update allowed fields' : 'Add a new bank account'}</p></div></div><button onClick={resetForm} className="p-2 rounded-xl bg-white/20 text-white hover:bg-white/30"><X size={18} /></button></div></div></div>
                                <div className="p-8 space-y-4">
                                    {/* Edit Notice */}
                                    {editingAccount && (
                                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
                                            <strong>Note:</strong> Bank name, account number, opening balance, and business/branch assignment cannot be changed after creation.
                                        </div>
                                    )}

                                    {/* Business and Branch selectors - disabled when editing */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1.5">Business (Optional)</label>
                                            <select
                                                value={form.businessId}
                                                onChange={(e) => handleBusinessChange(e.target.value)}
                                                disabled={!!editingAccount}
                                                className="w-full px-3 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 disabled:bg-slate-50 disabled:text-slate-400"
                                            >
                                                <option value="">No business</option>
                                                {(businesses || []).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1.5">Branch (Optional)</label>
                                            <select
                                                value={form.branchId}
                                                onChange={(e) => updateFormField('branchId', e.target.value)}
                                                disabled={!!editingAccount || !form.businessId}
                                                className="w-full px-3 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 disabled:bg-slate-50 disabled:text-slate-400"
                                            >
                                                <option value="">No branch</option>
                                                {branchesForBusiness(form.businessId).map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Bank Name - disabled when editing */}
                                    <Input label="Bank Name *" value={form.bankName} error={formErrors.bankName} onChange={e => updateFormField('bankName', e.target.value)} placeholder="e.g., Access Bank" required disabled={!!editingAccount} />

                                    <div className="grid grid-cols-2 gap-4">
                                        {/* Account Number - disabled when editing */}
                                        <Input label="Account Number *" value={form.accountNumber} error={formErrors.accountNumber} onChange={e => updateFormField('accountNumber', e.target.value)} placeholder="1234567890" required disabled={!!editingAccount} />
                                        <Input label="Account Name *" value={form.accountName} error={formErrors.accountName} onChange={e => updateFormField('accountName', e.target.value)} placeholder="Business Name" required />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1.5">Type</label>
                                            <select value={form.accountType} onChange={e => updateFormField('accountType', e.target.value)} className="w-full px-3 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white">
                                                <option value="savings">Savings</option>
                                                <option value="current">Current</option>
                                                <option value="cash">Cash</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                        <Input label="Opening Balance" type="number" value={form.openingBalance} onChange={e => updateFormField('openingBalance', e.target.value)} placeholder="0.00" disabled={!!editingAccount} />
                                    </div>

                                    <label className="flex items-center gap-3 cursor-pointer p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                                        <input type="checkbox" checked={form.isDefault} onChange={e => updateFormField('isDefault', e.target.checked)} className="w-4 h-4 rounded border-emerald-300 text-emerald-600" />
                                        <div><span className="text-sm font-medium text-emerald-800">Set as default account</span></div>
                                    </label>
                                </div>
                                <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 rounded-b-3xl px-8 py-4 flex justify-end gap-3">
                                    <Button variant="secondary" onClick={resetForm}>Cancel</Button>
                                    <Button onClick={() => saveMutation.mutate()} isLoading={saveMutation.isPending} disabled={!form.bankName || !form.accountNumber || !form.accountName} leftIcon={editingAccount ? <Save size={16} /> : <Rocket size={16} />}>{editingAccount ? 'Update' : 'Create'} Account</Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* DELETE CONFIRMATION */}
                <AnimatePresence>
                    {deleteConfirm && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setDeleteConfirm(null)}>
                            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} onClick={e => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8 text-center">
                                <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-4"><Trash2 size={28} className="text-rose-500" /></div>
                                <h3 className="text-xl font-bold text-slate-900">Delete Account?</h3>
                                <p className="text-sm text-slate-500 mt-2">This will permanently remove <strong>{deleteConfirm.bankName}</strong> ({deleteConfirm.accountNumber}).</p>
                                {parseFloat(deleteConfirm.currentBalance) > 0 && <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl"><p className="text-sm text-amber-700 flex items-center gap-2"><Sparkles size={16} className="text-amber-500" />Balance: <strong>{formatNaira(deleteConfirm.currentBalance)}</strong></p></div>}
                                <div className="flex gap-3 mt-8"><Button variant="secondary" onClick={() => setDeleteConfirm(null)} className="flex-1">Cancel</Button><Button variant="danger" onClick={() => deleteMutation.mutate(deleteConfirm.id)} isLoading={deleteMutation.isPending} className="flex-1">Delete</Button></div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}